import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
	Icons,
	IFilter,
	ISelect,
	ITableCell,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	IToolbar,
} from '@tecalisdev/infinity';
import { LanguageService } from '../../core/services/language.service';
import { RolePermissionsService } from '../../core/services/role-permissions.service';
import {
	endOfLocalToday,
	formatCellInColumn,
	formatDate,
	truncateUUID,
	isNotAvailableDisplay,
	notAvailableLabel,
} from '../../core/utils/utils';
import { MasterService } from '../../shared/services/master.service';
import { IUserActivityListItem, IUserActivityMetadata } from '../models/user-activity.models';

/** Valores alineados con `Tecalis\IdentityModels\Enums\UserActivityType` */
export const USER_ACTIVITY_TYPES: readonly { id: string }[] = [
	{ id: 'users' },
	{ id: 'companies' },
	{ id: 'api_keys' },
	{ id: 'design' },
	{ id: 'policy_checks' },
	{ id: 'workflows' },
	{ id: 'processes' },
	{ id: 'operations' },
	{ id: 'identities' },
	{ id: 'security' },
];

@Injectable({
	providedIn: 'root',
})
export class ActivityHelperService {
	private trans = inject(TranslateService);
	private masterService = inject(MasterService);
	private langSvc = inject(LanguageService);
	private rolePermissions = inject(RolePermissionsService);
	private usersSignal = signal<ISelect[]>([]);

	private get visibleActivityTypeIds(): string[] {
		return USER_ACTIVITY_TYPES.map((t) => t.id).filter((id) => this.canViewActivityType(id));
	}

	public readonly canFilterByUser = computed(() => this.rolePermissions.canViewUsers());

	public readonly canFilterByCompany = computed(() => {
		return (
			this.rolePermissions.isSuperadmin() &&
			this.rolePermissions.canViewCompanies() &&
			(this.masterService.companies?.length ?? 0) > 1
		);
	});

	public setUserOptions(value: ISelect[]): void {
		this.usersSignal.set(value ?? []);
	}

	readonly toolbar: Signal<IToolbar> = computed<IToolbar>(() => {
		this.langSvc.currentLang();

		const filters: IFilter[] = [];

		const typeOptions = this.visibleActivityTypeIds.map((id) => ({
			id,
			label: this.trans.instant(`ACTIVITY.TYPE.${id}`),
		}));

		if (typeOptions.length > 1) {
			filters.push({
				type: 'select',
				title: this.trans.instant('ACTIVITY.FILTERS.TYPE'),
				name: 'type',
				placeholder: this.trans.instant('ACTIVITY.FILTERS.TYPE'),
				options: typeOptions,
				search: true,
			});
		}

		if (this.canFilterByUser()) {
			filters.push({
				type: 'select',
				title: this.trans.instant('ACTIVITY.FILTERS.USER'),
				name: 'user_uuid',
				placeholder: this.trans.instant('ACTIVITY.FILTERS.USER'),
				options: this.usersSignal(),
				search: true,
			});
		}

		if (this.canFilterByCompany()) {
			filters.push({
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY'),
				options: this.masterService.companies,
				search: true,
			});
		}

		filters.push(
			{
				type: 'date',
				title: this.trans.instant('FILTERS.FROM'),
				name: 'datetime_from',
				placeholder: this.trans.instant('FILTERS.FROM'),
				labelChip: this.trans.instant('FILTERS.FROM'),
				nameLimitMaxDate: 'datetime_to',
				rowWith: ['datetime_from', 'datetime_to'],
				maxDate: endOfLocalToday(),
			},
			{
				type: 'date',
				title: this.trans.instant('FILTERS.TO'),
				name: 'datetime_to',
				placeholder: this.trans.instant('FILTERS.TO'),
				labelChip: this.trans.instant('FILTERS.TO'),
				nameLimitMinDate: 'datetime_from',
				rowWith: ['datetime_from', 'datetime_to'],
				maxDate: endOfLocalToday(),
			},
		);

		return {
			filters,
			search: {
				placeholder: this.trans.instant('ACTIVITY.SEARCH'),
				name: 'search',
			},
		};
	});

	get header(): ITableHeader[] {
		return [
			{ label: this.trans.instant('ACTIVITY.TABLE.ACTIVITY') },
			{ label: this.trans.instant('ACTIVITY.TABLE.ELEMENT') },
			{ label: this.trans.instant('ACTIVITY.TABLE.IP') },
			{ label: this.trans.instant('ACTIVITY.TABLE.ENVIRONMENT') },
			{ label: this.trans.instant('ACTIVITY.TABLE.USER') },
			{
				label: this.trans.instant('ACTIVITY.TABLE.DATE'),
				sortable: true,
				sortKey: 'occurred_at',
				align: 'right',
			},
		];
	}

	get config(): ITableConfig {
		return {
			clickeable: false,
			pagination: {
				page: 1,
				limit: 25,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	get sort(): ITableSortEvent {
		return { ...this.resetSort() };
	}

	public resetSort(): ITableSortEvent {
		return {
			sortKey: 'occurred_at',
			sortDirection: 'desc',
			headerIndex: this.header.findIndex((h) => h.sortKey === 'occurred_at') || 0,
		};
	}

	public transformRow(response: IUserActivityListItem[]): ITableRow[] {
		return response.map((row) => this.transformListTableRow(row));
	}

	public transformListTableRow(row: IUserActivityListItem): ITableRow {
		const activityLabel = this.getActivityLabel(row);
		const companySecondary =
			this.canFilterByCompany() && row.company?.name ? this.getCompanyLabel(row.company.name) : undefined;
		const element = this.resolveElementLabels(row);
		const userName =
			[row.user?.name, row.user?.surname].filter((part) => !!part?.trim()).join(' ').trim() ||
			row.user?.email?.trim() ||
			notAvailableLabel(this.trans);

		const device = row.device?.trim() || '';
		const explorer = row.explorer?.trim() || '';
		const environmentLabel =
			formatCellInColumn(device, explorer) || notAvailableLabel(this.trans);

		const cells: ITableCell[] = [
			{
				label: activityLabel,
				secondaryLabel: companySecondary,
				// tag-icon `default` del DS es naranja; tag-image `simple` = fondo blanco (Figma / listado previo).
				tagImage: {
					icon: this.getTypeIcon(row.type),
					status: 'simple',
				},
			},
			{
				label: element.label,
				...(element.secondary ? { secondaryLabel: this.toElementLinkLabel(element.secondary, row.link) } : {}),
			},
			{ label: row.ip?.trim() || notAvailableLabel(this.trans) },
			{ label: environmentLabel },
			this.transformUserCell(row, userName),
			{ label: formatDate(row.occurred_at), align: 'right' },
		];

		return {
			clickValue: row,
			cells,
		};
	}

	/**
	 * Avatar del DS + nombre completo como `label`.
	 * `tec-table` no pinta `label` solo con `avatar`, y `avatar-group` fuerza `showName=false`.
	 * Un `tagImage` oculto por CSS dispara `textlabelRichTip` para mostrar el nombre.
	 * Tooltip del email: `IAvatar.tooltip` (hover del círculo; size default) + `cell.tooltip` (hover del label).
	 */
	private transformUserCell(row: IUserActivityListItem, userName: string): ITableCell {
		if (isNotAvailableDisplay(userName)) {
			return { label: notAvailableLabel(this.trans) };
		}

		const email = row.user?.email?.trim() || undefined;

		return {
			label: this.escapeHtml(userName),
			classes: ['activity-user-cell'],
			...(email ? { tooltip: email } : {}),
			avatar: {
				avatars: [
					{
						id: row.user?.uuid || row.uuid,
						name: userName,
						showName: true,
						...(email ? { tooltip: email } : {}),
					},
				],
				maxAvatarToShow: 1,
			},
			tagImage: {
				icon: 'icon-user',
				status: 'simple',
			},
		};
	}

	private escapeHtml(value: string): string {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	/** ID secundario de Elemento: enlace interno si `link` es ruta relativa segura. */
	private toElementLinkLabel(text: string, link?: string | null): string {
		const safeText = this.escapeHtml(text);
		const href = this.toSafeInternalHref(link);
		if (!href) {
			return safeText;
		}

		return `<a href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
	}

	/** Solo rutas relativas de la app (`/…`); rechaza `//`, `javascript:`, etc. */
	private toSafeInternalHref(link?: string | null): string | null {
		const value = (link ?? '').trim();
		if (!value.startsWith('/') || value.startsWith('//')) {
			return null;
		}
		return value;
	}

	public getTypeIcon(type?: string | null): Icons {
		const iconMap: Record<string, Icons> = {
			users: 'icon-user',
			companies: 'icon-briefcase',
			api_keys: 'icon-lock',
			design: 'icon-customize',
			workflows: 'icon-workflow',
			processes: 'icon-list-ticks',
			operations: 'icon-pending-check',
			identities: 'icon-users',
			legal_terms: 'icon-check-circle',
			policy_checks: 'icon-check-circle',
			security: 'icon-lock',
		};

		return iconMap[String(type)] || 'icon-list-ticks';
	}

	public getCompanyLabel(name?: string | null): string {
		const value = (name || this.trans.instant('FILTERS.COMPANY')).trim();
		return `${value}`;
	}

	public getUserInitials(name?: string | null, email?: string | null): string {
		const source = (name || email || '').trim();
		if (!source) return '--';

		const parts = source
			.split(/[\s@._-]+/)
			.map((part) => part.trim())
			.filter(Boolean);

		if (parts.length === 0) return '--';
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
	}

	public getActivityLabel(activity: IUserActivityListItem): string {
		const bold = this.resolveSegment(activity, 'bold');
		const middle = this.resolveSegment(activity, 'middle');
		return `${bold}${middle}`.trim();
	}

	private resolveSegment(activity: IUserActivityListItem, part: 'bold' | 'middle'): string {
		const key = this.activitySegmentKey(activity, part);
		const msg = this.trans.instant(key);
		if (msg !== key) return msg;
		return this.trans.instant(`ACTIVITY.SEGMENT._fallback.${part}`);
	}

	private activitySegmentKey(activity: IUserActivityListItem, part: 'bold' | 'middle'): string {
		const rawType = String(activity.type ?? '').trim();
		const type = rawType !== '' ? rawType.replace(/-/g, '_').toLowerCase() : '_unknown';
		const rawAction = String(activity.action ?? '')
			.trim()
			.toLowerCase();
		const action = rawAction !== '' ? rawAction : '_unknown';
		return `ACTIVITY.SEGMENT.${type}.${action}.${part}`;
	}

	/**
	 * Columna ELEMENTO (Figma NIF-337):
	 * - Con `metadata.name` → nombre arriba, ID abajo.
	 * - Sin nombre pero con `metadata.document_number` → nº documento arriba, ID abajo.
	 * - Sin metadata (histórico / operaciones) → solo ID (reference o subject_uuid corto).
	 */
	private resolveElementLabels(activity: IUserActivityListItem): { label: string; secondary?: string } {
		const meta = this.parseMetadata(activity.metadata);
		const name = (meta?.name ?? '').trim();
		const documentNumber = (meta?.document_number ?? '').trim();
		const id = this.resolveElementId(activity);

		if (name) {
			return {
				label: this.formatElementPrimary(name),
				...(id && id !== name ? { secondary: id } : {}),
			};
		}

		if (documentNumber) {
			return {
				label: this.formatElementPrimary(documentNumber),
				...(id && id !== documentNumber ? { secondary: id } : {}),
			};
		}

		return { label: id || notAvailableLabel(this.trans) };
	}

	private parseMetadata(raw: IUserActivityMetadata | null | undefined): IUserActivityMetadata | null {
		if (!raw || typeof raw !== 'object') {
			return null;
		}
		return raw;
	}

	/** Misma convención visual que compañía: valor entre corchetes. */
	private formatElementPrimary(value: string): string {
		return `${value}`;
	}

	/** Preferir `reference` del API; si falta, acortar `subject_uuid` como el back. */
	private resolveElementId(activity: IUserActivityListItem): string {
		const reference = (activity.reference ?? '').trim();
		if (reference) {
			return reference;
		}

		const subject = (activity.subject_uuid ?? '').trim();
		if (!subject) {
			return '';
		}

		return truncateUUID(subject, 4, 2);
	}

	private canViewActivityType(type: string): boolean {
		switch (type) {
			case 'users':
				return this.rolePermissions.canViewUsers();
			case 'companies':
				return this.rolePermissions.canViewCompanies();
			case 'api_keys':
				return this.rolePermissions.canViewApiKeys();
			case 'design':
				return this.rolePermissions.canViewDesignConfig();
			case 'workflows':
				return this.rolePermissions.canViewWorkflows();
			case 'processes':
				return this.rolePermissions.canViewProcesses();
			case 'operations':
				return this.rolePermissions.canViewOperations();
			case 'identities':
				return this.rolePermissions.canViewPersons();
			case 'legal_terms':
			case 'policy_checks':
				return this.rolePermissions.canViewPolicyChecks();
			case 'security':
				return this.rolePermissions.canViewFaces();
			default:
				return false;
		}
	}
}
